import { FETCH_CONDITION_START, FETCH_CONDITION_SUCCESS, FETCH_CONDITION_FAIL } from './actionTypes';
import fetchData from './fetchData';
import { fetchDetailsSuccess } from './details';
import { fetchListSuccess } from './list';
import { setGlobalState, fetchPaymentOptions, login, fetchCart, updateCartPricing } from './index';

/* * * * * * * * * * * * * * *
* ACTIVE ACTION TYPE METHODS *
* * * * * * * * * * * * * *  */
// action type methods return current active action type that is determined by the state of the fetch requests.
// Also these methods pass data passed from user methods to Redux reducers to update states
export const fetchConditionStart = (identifier) => {
    return {
        type: FETCH_CONDITION_START,
        identifier: identifier
    };
};

export const fetchConditionSuccess = (identifier, value = true) => {
    return {
        type: FETCH_CONDITION_SUCCESS,
        identifier: identifier,
        value: value
    };
};

export const fetchConditionFail = (identifier, error = 'Error message missing. Please contact site administrator.') => {
    return {
        type: FETCH_CONDITION_FAIL,
        identifier: identifier,
        error: error
    };
};

/* * * * * * * * * * * *  *
* USER ACCESSIBLE METHODS *
* * * * * * * * * * * * * */
// User accessible methods are exported and can be accessed across the whole project.
// These methods execute fetch calls and dispatch correct method that updates active action type according the state of the request


/* * * * * * * * * * * * * * * *
* SEND EMAIL VERIFICATION LINK *
* * * * * * * * * * * * * * *  */
export const sendEmailVerification = (emailAddress = null, communityId = null, redirectUrl = null) => {
    const bodyData = !!emailAddress && !!communityId && !!redirectUrl ? {
        emailAddress: emailAddress,
        communityId: communityId,
        redirectUri: redirectUrl
    } : {};
    return async dispatch => {
        dispatch(fetchConditionStart('verification'));
        try {
            await fetchData('POST', 'emailVerification/start', bodyData);
            dispatch(setGlobalState('verifyEmail', null));
            dispatch(fetchConditionSuccess('verification', true));
        } catch (error) {
            dispatch(fetchConditionFail('verification', error));
        }

    };
};

/* * * * * * * * * * *  *
* CONFIRM EMAIL ADDRESS *
* * * * * * * *  * * *  */
export const confirmEmailAddress = (emailAddress = null, code = null) => {
    const bodyData = !!emailAddress && !!code ? {
        emailAddress: emailAddress,
        code: code
    } : {};
    return async dispatch => {
        dispatch(fetchConditionStart('confirmEmail'));
        try {
            await fetchData('POST', 'emailVerification/confirm', bodyData);
            dispatch(fetchConditionSuccess('confirmEmail', true));
        } catch (error) {
            dispatch(fetchConditionFail('confirmEmail', error));
        }
    };
};


// USER LOGOUT
export const logout = () => {
    localStorage.removeItem('userData');
    clearTimeout(window.tokenTimeoutId);
    return async dispatch => {
        dispatch(fetchConditionStart('logout'));
        try {
            await fetchData('POST', 'logout');
            dispatch(fetchConditionSuccess('logout', true));
        } catch (error) {
            dispatch(fetchConditionFail('logout', error));
        }
    };
};

// USER VALIDATION METHODS
export const checkTokenTimeout = (delaySeconds = 10) => {
    return dispatch => {
        clearTimeout(window.tokenTimeoutId);
        window.tokenTimeoutId = setTimeout(() => {
            dispatch(checkTokenValidation());
        }, delaySeconds * 1000);
    };
};

export const checkTokenValidation = () => {
    return async dispatch => {
        try {
            const user = await fetchData('GET', 'heartbeat');

            !!user && user.is_logged_in ? dispatch(checkTokenTimeout()) : dispatch(logout());
        } catch (error) {
            // dispatch(logout());
        }
    };
};

const getUserData = () => localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')) : null;

export const checkUserDataValidation = () => {
    return dispatch => {
        const userData = getUserData();

        dispatch(fetchDetailsSuccess('user', userData));
        if (!!userData) {
            dispatch(checkTokenValidation());
        }
    };
};

// UPDATE USER PREFERENCES - PATCH
export const updateUserPreferences = (userId = null, value = null, path = null) => {
    const bodyData = path ? [{
        value: value,
        path: path,
        op: 'replace'
    }] : [{}];

    return async dispatch => {
        dispatch(fetchConditionStart('userPreference'));
        try {
            const userData = await fetchData('PATCH', `users/${userId}`, bodyData);
            localStorage.removeItem('userData');
            localStorage.setItem('userData', JSON.stringify(userData));
            dispatch(fetchDetailsSuccess('user', userData));
            dispatch(fetchConditionSuccess('userPreference', true));
        } catch (error) {
            dispatch(fetchConditionFail('userPreference', error));
        }
    };
};

// UPDATE USER DETAILS - PUT
export const updateUserDetails = (userId = null, userDetails = {}) => {
    return async dispatch => {
        dispatch(fetchConditionStart('userUpdate'));
        try {
            const userData = await fetchData('PUT', `users/${userId}`, userDetails);
            localStorage.removeItem('userData');
            localStorage.setItem('userData', JSON.stringify(userData));
            dispatch(fetchDetailsSuccess('user', userData));
            dispatch(fetchConditionSuccess('userUpdate', true));
        } catch (error) {
            dispatch(fetchConditionFail('userUpdate', error));
        }
    };
};

// USER SIGNUP
export const signup = (name = null, email = null, communityId = null, url = null) => {
    const bodyData = name && email && url ? {
        name: name,
        emailAddress: email,
        communityId: communityId,
        redirectUri: `${url}/set-password`
    } : {};

    return async dispatch => {
        dispatch(fetchConditionStart('signup'));
        try {
            await fetchData('POST', 'users', bodyData);
            dispatch(fetchConditionSuccess('signup', true));
        } catch (error) {
            dispatch(fetchConditionFail('signup', error));
        }
    };
};

// USER SET PASSWORD
export const setPassword = (email = null, code = null, password = null) => {
    const bodyData = email && code && password ? {
        emailAddress: email,
        code: code,
        password: password
    } : {};
    return async dispatch => {
        dispatch(fetchConditionStart('setPassword'));
        try {
            await fetchData('POST', 'passwordReset/confirm', bodyData);
            dispatch(fetchConditionSuccess('setPassword', true));
            dispatch(login(email, password, '/'));
        } catch (error) {
            dispatch(fetchConditionFail('setPassword', error));
        }
    };
};

// USER RESET PASSWORD
export const resetPassword = (email = null, communityId = null, redirectUrl = null) => {
    const bodyData = email && redirectUrl ? {
        emailAddress: email,
        communityId: communityId,
        redirectUri: `${redirectUrl}/set-password`
    } : {};
    return async dispatch => {
        dispatch(fetchConditionStart('resetPassword'));
        try {
            await fetchData('POST', 'passwordReset/start', bodyData);
            dispatch(fetchConditionSuccess('resetPassword', true));
        } catch (error) {
            dispatch(fetchConditionFail('resetPassword', error));
        }
    };
};

// USER UPDATE PASSWORD
export const updatePassword = (userId = null, oldPassword = null, newPassword = null) => {
    const bodyData = oldPassword && newPassword ? {
        oldPassword: oldPassword,
        newPassword: newPassword
    } : {};
    return async dispatch => {
        dispatch(fetchConditionStart('password'));
        try {
            await fetchData('PUT', `users/${userId}/password`, bodyData);
            dispatch(fetchConditionSuccess('password', true));
        } catch (error) {
            dispatch(fetchConditionFail('password', error));
        }
    };
};


/* * * * * * * * *  *
* ADD NEW CART ITEM *
* * * * * * * * * * */
export const addCartItem = (itemId = null, paymentAccountId = null, start = null, end = null, numOfPeople = 1, quantity = 1, reference = null, comment = null, operatingCompany = null) => {
    const cartItemBody = itemId && start && end ? { itemId: itemId, periodStart: start, periodEnd: end, numberOfPeople: numOfPeople, quantity: quantity } : {};
    if (comment) {
        cartItemBody.comment = comment;
    }
    if (reference) {
        cartItemBody.reference = reference;
    }
    if (operatingCompany) {
        cartItemBody.operatingCompany = operatingCompany;
    }
    return async dispatch => {
        dispatch(fetchConditionStart('addCartItem'));
        try {
            await fetchData('POST', `paymentAccounts/${paymentAccountId}/cart/items`, cartItemBody);
            dispatch(fetchCart(paymentAccountId, null));
            dispatch(fetchConditionSuccess('addCartItem', true));
        } catch (error) {
            dispatch(fetchConditionFail('addCartItem', error));
        }
    };
};

export const updateCartItem = (paymentAccountId = null, cartItemId = null, updatedProperties = {}, cartData) => {
    const patches = Object.entries(updatedProperties).map((prop) => ({ 'value': prop[1], 'path': prop[0], 'op': 'replace' }));

    return async dispatch => {
        dispatch(setGlobalState('activeCartItem', cartItemId));
        dispatch(fetchConditionStart('updateCartItem'));
        try {
            const data = await fetchData('PATCH', `paymentAccounts/${paymentAccountId}/cart/items/${cartItemId}`, patches);
            const updatedCartItems = cartData.cartItems.map(item => item.id === cartItemId ? data : item);
            const updatedCartData = { ...cartData, ...{ cartItems: updatedCartItems } };
            dispatch(fetchDetailsSuccess('cart', updatedCartData));
            dispatch(fetchConditionSuccess('updateCartItem', true));
            dispatch(setGlobalState('activeCartItem', null));
        } catch (error) {
            dispatch(fetchConditionFail('updateCartItem', error));
            dispatch(setGlobalState('activeCartItem', null));
        }
    };
};

export const removeCartItem = (paymentAccountId = null, cartItemId = null, cartData) => {
    return async dispatch => {
        dispatch(setGlobalState('activeCartItem', cartItemId));
        dispatch(fetchConditionStart('removeCartItem'));
        try {
            await fetchData('DELETE', `paymentAccounts/${paymentAccountId}/cart/items/${cartItemId}`);

            const updatedCartItems = cartData.cartItems.filter(item => item.id !== cartItemId);
            const updatedCartData = { ...cartData, ...{ cartItems: updatedCartItems } };
            dispatch(fetchDetailsSuccess('cart', updatedCartData));

            if (updatedCartItems.length) {
                dispatch(fetchPaymentOptions(paymentAccountId));
            } else {
                dispatch(fetchListSuccess('paymentOptions', null));
                dispatch(fetchDetailsSuccess('cartPricing', null));
            }

            dispatch(fetchConditionSuccess('removeCartItem', true));
            dispatch(setGlobalState('activeCartItem', null));
        } catch (error) {
            dispatch(fetchConditionFail('removeCartItem', error));
            dispatch(setGlobalState('activeCartItem', null));
        }
    };
};

export const clearCart = (paymentAccountId = null, paymentMethodId = null) => {
    return async dispatch => {
        dispatch(fetchConditionStart('clearCart'));
        try {
            await fetchData('DELETE', `paymentAccounts/${paymentAccountId}/cart`);
            dispatch(fetchCart(paymentAccountId, paymentMethodId));
            dispatch(fetchConditionSuccess('clearCart', true));
        } catch (error) {
            dispatch(fetchConditionFail('clearCart', error));
        }
    };
};

/* * * * * * * *  *
* VOUCHER METHODS *
* * * * * * * * * */
export const removeVoucher = (paymentAccountId = null, cartItemId = null, paymentMethodId = null, cartData) => {
    return async dispatch => {
        dispatch(setGlobalState('activeCartItem', cartItemId));
        dispatch(fetchConditionStart('removeVoucher'));
        try {
            await fetchData('DELETE', `paymentAccounts/${paymentAccountId}/cart/items/${cartItemId}/voucher`);
            const updatedCartItems = cartData.cartItems.map(item => {
                const { voucher, ...newItem} = item;
                return newItem;
            });
            const updatedCartData = { ...cartData, ...{ cartItems: updatedCartItems } };
            dispatch(fetchDetailsSuccess('cart', updatedCartData));
            dispatch(updateCartPricing(paymentAccountId, paymentMethodId));
            dispatch(fetchConditionSuccess('removeVoucher', true));
            dispatch(setGlobalState('activeCartItem', null));
        } catch (error) {
            dispatch(fetchConditionFail('removeVoucher', error));
            dispatch(setGlobalState('activeCartItem', null));
        }
    };
};

/* * * * * * * * * * * * * * * *
* DIRECT DEBIT PAYMENT METHODS *
* * * * * * * * * * * * * * *  */
export const deleteDirectDebitDetails = (paymentMethodId = null) => {
    return async dispatch => {
        dispatch(fetchConditionStart('deleteDirectDebit'));
        try {
            await fetchData('DELETE', `directDebitPaymentMethods/${paymentMethodId}`);
            dispatch(fetchConditionSuccess('deleteDirectDebit', true));
        } catch (error) {
            dispatch(fetchConditionFail('deleteDirectDebit', error));
        }
    };
};



// WEBPACK FOOTER //
// ./src/store/actions/condition.js